import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, generatePath } from 'react-router-dom'
import { Row, Col, Card, Button, Modal, Table, Form } from 'react-bootstrap'

import { API, Auth, Router, Flash, useGlobalContext } from 'app'
import { MediumContainer } from 'component/Layout'
import { useDataLoader, ResourceAccessButton, BackCrumbs } from 'component/Util'

const nl2brFragment = (text) => {
    if (!text) return null;
    const parts = text.split(/(?:\r\n|\r|\n)/);
    return parts.map((part, index) => (
        <React.Fragment key={index}>
            {part}
            {index < parts.length - 1 && <br />}
        </React.Fragment>
    ));
}

const TestsessionCreateModalBody = (props) => {
    const navigate = useNavigate()
    const [sections, setSections] = useState([])
    const [selectedTime, setSelectedTime] = useState('suggested')
    const [customTimeMinutes, setCustomTimeMinutes] = useState('')
    const callApi = API.useAPI()
    const res = props.params.res
    useEffect(() => {
        var sections = structuredClone(res.contents.source_params.sections)
        for (var section of sections) {
            section['selected'] = true
        }
        setSections(sections)
    }, [])
    const handleSelectionChange = (event) => {
        const section_index = event.target.getAttribute('data-section-index')
        setSections(prevSections => prevSections.map(
            (section, idx) => (idx == section_index) ? { ...section, selected: event.target.checked } : section
        ))
    }
    const handleCustomTimeChange = (event) => {
        setCustomTimeMinutes(event.target.value)
    }
    const isCustomTimeValid = selectedTime == 'suggested' || (Number.isInteger(Number(customTimeMinutes)) && Number(customTimeMinutes) >= 5 && Number(customTimeMinutes) <= 180)

    const selectedSectionIds = sections.reduce((ids, section, index) => {
        if (section.selected) ids.push(index)
        return ids
    }, [])
    const selectedSections = sections.filter((section) => section.selected)
    const suggestedTime = selectedSections.reduce((acc, section) => acc + (section.selected ? section.suggested_time : 0), 0)
    const createTestsession = async () => {
        console.log('createTestSession')
        try {
            const user_params = {
                section_ids: selectedSectionIds,
                duration: (selectedTime == 'suggested') ? suggestedTime : customTimeMinutes * 60
            }
            const { handle } = await callApi(API.endpoints.TESTSESSION_CREATE, { course_name: props.params.course_name, resIdx: props.params.resIdx, user_params })
            navigate(generatePath(Router.routes.TESTSESSION.path, { handle }))
        } catch (err) {
            console.log(err)
            return
        }
    }

    return <>
        <Modal.Header closeButton>
            <Modal.Title>{res.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <div>Select one or more sections:</div>
            <Table>
                <thead>
                    <tr>
                        <th>Section</th>
                        <th>Number of problems</th>
                        <th>Suggested time (minutes)</th>
                        <th>Selected</th>
                    </tr>
                </thead>
                <tbody>
                    {res.contents.source_params.sections.map((section, idx) =>
                        <tr key={idx}>
                            <td>{section.title ?? 'Main'}</td>
                            <td>{section.problem_ids.length}</td>
                            <td>{section.suggested_time / 60}</td>
                            <td><Form.Check type='checkbox' checked={sections?.[idx]?.selected ?? true} data-section-index={idx} onChange={handleSelectionChange}></Form.Check></td>
                        </tr>
                    )}
                </tbody>
            </Table>
            Test duration:
            <Row>
                <Col>
                    <Form.Check type='radio' name='test_duration' value='suggested' label={`Use suggested time: ${suggestedTime / 60} minutes`} checked={selectedTime == 'suggested'} onChange={() => setSelectedTime('suggested')}></Form.Check>
                </Col>
            </Row>
            <Row>
                <Col>
                    <Form.Check type='radio' name='test_duration' value='custom' checked={selectedTime == 'custom'} onChange={() => setSelectedTime('custom')} inline label={
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            Set a custom time (5-180 minutes):
                            <Form.Control type='text' size='sm' value={customTimeMinutes} onChange={handleCustomTimeChange} isInvalid={!isCustomTimeValid} maxLength={3} style={{ width: '10ch' }} disabled={selectedTime == 'suggested'}></Form.Control>
                        </div>
                    }></Form.Check>
                </Col>
            </Row>
            <Button variant='success' onClick={createTestsession} disabled={(selectedSections.length == 0) || !isCustomTimeValid}>Create test session</Button>
        </Modal.Body>
    </>
}

const CourseStatusPanel = (props) => {
    const userRole = Auth.useGetRole()
    const { allowed, expires_on } = Auth.useCheckCourseAccess(props.course_name)
    const callApi = API.useAPI()
    const navigate = useNavigate()
    const { googleLoginDomRef } = useGlobalContext()

    const dateStringOptions = {
        timeZone: "Asia/Kolkata",
        year: "numeric",
        month: "long",
        day: "numeric",
    }

    const subscriptionFlow = async () => {
        try {
            var { sub } = await callApi(API.endpoints.SUBSCRIPTION_CREATE, { course_name: props.course_name })
            var subHandle = sub.handle
            if (sub.status == 'ACTIVE') {
                Flash.add({ type: 'WARNING', message: 'Subscription is already active' })
            } else if (sub.status == 'PENDING') {
                const order = sub.order_info.order
                const rzp = new Razorpay({
                    key: process.env.REACT_APP_RAZORPAY_KEY_ID,
                    amount: order.amount,
                    currency: order.currency,
                    name: 'Sohani Academy',
                    description: `Subscription for course: ${props.course_name}`,
                    order_id: order.id,
                    handler: async (response) => {
                        Flash.add({ type: 'WARNING', message: 'Verifying payment, please wait...' })
                        var { sub } = await callApi(API.endpoints.SUBSCRIPTION_VERIFY, {
                            handle: subHandle,
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature
                        })
                        if (sub.status == 'ACTIVE') {
                            Flash.add({ type: 'SUCCESS', message: 'Payment successful! Reloading to activate, please wait...' })
                            const sleep = ms => new Promise(r => setTimeout(r, ms))
                            await sleep(3000)
                            navigate(0)
                        } else {
                            Flash.add({ type: 'ERROR', message: 'Payment flow did not complete. Please reload the page and check again' })
                        }
                    },
                    // todo: add prefill
                })
                rzp.on('payment.failed', function (response) {
                    Flash.add({ type: 'ERROR', message: 'Payment interrupted; please try again' })
                })
                rzp.open()
            } else {
                Flash.add({ type: 'ERROR', message: 'Invalid subscription status' })
            }
        } catch (err) {
            console.log(err)
            Flash.add({ type: 'ERROR', message: 'Error in subscription flow' })
            return
        }
    }
    // const handleSigninClick = (e) => {
    //     e.preventDefault()
    //     googleLoginDomRef?.current?.click()
    // }
    // const signinLink = (googleLoginDomRef?.current) ? <a href='#' onClick={handleSigninClick} style={{ color: 'blue', textDecoration: 'underline', cursor: 'pointer' }}>sign in with Google</a> : <span>sign in with Google</span>
    const signinLink = <span>sign in with Google</span>
    // Possible states: 
    // ROLE_ANON
    // ROLE_USER, no active subscription
    // ROLE_USER, active subscription
    // ROLE_USER, expired subscription
    // ROLE_GOLD
    const userBanner = () => {
        if (Auth.isRoleAtleast(userRole, 'ROLE_GOLD')) {
            return <>
                <span className='text-success'><b>You have a gold subscription!</b></span> <br />
                You can access all the course material below.
            </>
        } else if (userRole == 'ROLE_USER') {
            if (allowed) {
                return <>
                    <span className='text-success'><b>Your have an active subscription to this course till {new Date(expires_on).toLocaleString('en-IN', dateStringOptions)}.</b></span> <br />
                    You can access all the course material below.
                </>
            } else {
                return <>
                    <span className='text-danger'><b>You have not subscribed to this course.</b></span> <br />
                    You can access the free resources below. <br />
                    Please subscribe to the course, to access the entire course material.<br />
                    The subscription costs <b>Rs 1000/- inclusive of all taxes</b> and remains active for <b>365 days</b> from the date of purchase.<br />
                    <Button variant="primary" size="sm" onClick={subscriptionFlow}>Pay and subscribe now</Button>
                </>
            }
        } else if (userRole == 'ROLE_ANON') {
            return <>
                <span className='text-danger'><b>You have not signed in.</b></span> <br />
                You can access some of the free resources below. <br />
                Please {signinLink} to access all the free resources. <br />
                Please subscribe to the course, to access the entire course material.
            </>
        }
    }
    return <div>
        {userBanner()}
    </div>
}
export const CoursePage = () => {
    const params = useParams()
    const course_name = params.course_name
    const courseLoader = useDataLoader()
    const navigate = useNavigate()
    const [showTestsessionCreateModal, setShowTestsessionCreateModal] = React.useState(false)
    const [testsessionCreateParams, setTestsessionCreateParams] = React.useState(null)
    const handleClose = () => setShowTestsessionCreateModal(false)

    const testsessionConfigure = async (res, resIdx) => {
        setTestsessionCreateParams({ course_name, res, resIdx })
        setShowTestsessionCreateModal(true)
    }
    const getActions = (res, resIdx) => {
        var btnAction, btnLabel
        if (res.release_status != 'RELEASED') {
            return <Button size='sm' variant='outline-secondary' style={{ border: '1px dashed gray' }} disabled>Not yet released</Button>
        } else if (res.type == 'document') {
            btnAction = ((resIdx) => () => { navigate(generatePath(Router.routes.RESOURCE_VIEW.path, { course_name, resIdx })) })(resIdx)
            btnLabel = 'View document'
        } else if (res.type == 'problemset') {
            btnAction = ((resIdx) => () => { navigate(generatePath(Router.routes.RESOURCE_VIEW.path, { course_name, resIdx })) })(resIdx)
            btnLabel = 'Start practicing'
        } else {
            btnAction = ((res, resIdx) => () => { testsessionConfigure(res, resIdx) })(res, resIdx)
            btnLabel = 'Create test session...'
        }
        return <ResourceAccessButton course_name={course_name} resource={res} yesLabel={btnLabel} yesAction={btnAction} yesVariant="primary" />
    }

    useEffect(() => void courseLoader.fetch(API.endpoints.COURSE_VIEW, { course_name }), [])
    return courseLoader.view((data) => {
        var cards = []
        for (var resIdx in data.course.contents.resources) {
            var res = data.course.contents.resources[resIdx]
            const actions = getActions(res, resIdx)
            cards.push(
                <Card key={resIdx}>
                    <Card.Body>
                        <Card.Subtitle style={{ float: 'right' }}>
                            {actions}
                        </Card.Subtitle>
                        <Card.Title>
                            {res.title}
                        </Card.Title>
                        <Card.Text>
                            {nl2brFragment(res.description)}
                        </Card.Text>
                    </Card.Body>
                </Card>)
        }
        return <MediumContainer>
            <BackCrumbs path={generatePath(Router.routes.INDEX.path)} msg='Back to home' />
            <h3>{data.course.title}</h3>
            <div dangerouslySetInnerHTML={{ __html: data.course.description }} />
            <Modal size="lg" show={showTestsessionCreateModal} onHide={handleClose}>
                <TestsessionCreateModalBody params={testsessionCreateParams} />
            </Modal>
            <CourseStatusPanel course_name={course_name} />
            <br />
            {cards}
        </MediumContainer>
    })
}
